import type { AccountType } from '@pulsify/shared/auth-roles'
import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { authApi } from '~/entities/session/api/auth'
import * as m from '~/paraglide/messages'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { loginRouteForAccountType } from '~/shared/lib/login-route'
import { validatePasswordForForm } from '~/shared/lib/password-error-message'
import { NetworkError } from '~/shared/lib/rpc'
import { cn } from '~/shared/lib/utils'
import { resetPasswordSchema } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Label } from '~/shared/ui/label'
import { PasswordInput } from '~/shared/ui/password-input'
import { PasswordStrength } from '~/shared/ui/password-strength'
import { AuthHeading, AuthShell, authLinkClassName } from './auth-shell'

/**
 * Shared "set a password from an emailed link" flow for the invite
 * (`/setup-account`, GoTrue `type=invite`) and recovery (`/reset-password`,
 * `type=recovery`) pages. Both consume a Supabase access token from the URL
 * hash, set the password, and land the user on the right login. When the link
 * is invalid/expired (or the page is opened without a token), the password form
 * is not rendered at all - only the reason and a "request a new link" button -
 * since there is nothing the user can submit.
 */
interface SetPasswordFlowProps {
  /** GoTrue hash `type` this flow accepts. */
  tokenType: 'invite' | 'recovery'
  role: AccountType
  title: string
  description: string
  successTitle: string
  successDescription: string
  submitIdleLabel: string
  submitPendingLabel: string
  /** Muted guidance shown when the page is opened with neither token nor error. */
  noTokenHelp: string
  /** Render the "remember your password? sign in" footer (recovery page). */
  signInFooter?: boolean
}

function parseTokenHash(tokenType: 'invite' | 'recovery'): {
  token: string | null
  sessionError: string | null
} {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : ''
  const params = new URLSearchParams(hash)
  const errorDescription = params.get('error_description')
  if (errorDescription) return { token: null, sessionError: errorDescription }
  const accessToken = params.get('access_token')
  if (params.get('type') === tokenType && accessToken) {
    return { token: accessToken, sessionError: null }
  }
  return { token: null, sessionError: null }
}

export function SetPasswordFlow(props: SetPasswordFlowProps) {
  const navigate = useNavigate()
  // Lazy init so the correct state (form vs "request a new link") is right on
  // the first paint, with no flash from reading the hash a tick later.
  const [{ token, sessionError }] = useState(() => parseTokenHash(props.tokenType))
  const [isSuccess, setIsSuccess] = useState(false)

  // Strip the token from the address bar / history once mounted.
  useEffect(() => {
    if (token) window.history.replaceState({}, '', window.location.pathname)
  }, [token])

  const setPassword = useMutation({
    mutationFn: (newPassword: string) => {
      if (!token) throw new Error('missing token')
      return authApi.setPasswordWithToken(token, newPassword)
    },
    onSuccess: ({ email }) => {
      setIsSuccess(true)
      setTimeout(() => {
        navigate({ to: loginRouteForAccountType(props.role), search: { email, reason: '' } })
      }, 3000)
    },
  })

  const form = useForm({
    defaultValues: { newPassword: '', confirmPassword: '' },
    onSubmit: async ({ value }) => {
      await setPassword.mutateAsync(value.newPassword)
    },
  })

  if (isSuccess) {
    return (
      <AuthShell>
        <AuthHeading
          icon={<Check />}
          title={props.successTitle}
          description={props.successDescription}
        />
        <Button asChild variant="brand" size="lg" className="w-full">
          <Link to="/login" search={{ email: '', reason: '' }}>
            {m.goToLogin()}
          </Link>
        </Button>
      </AuthShell>
    )
  }

  const submitError = setPassword.isError
    ? setPassword.error instanceof NetworkError
      ? setPassword.error.getDialogContent().message
      : m.failedToResetPasswordPleaseCheckYourCodeAndTryAgain()
    : null

  return (
    <AuthShell
      footer={
        props.signInFooter ? (
          <p>
            {m.rememberYourPassword()}{' '}
            <Link to="/login" search={{ email: '', reason: '' }} className={authLinkClassName}>
              {m.signIn()}
            </Link>
          </p>
        ) : undefined
      }
    >
      <AuthHeading title={props.title} description={props.description} />
      {token ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field
            name="newPassword"
            validators={createFieldValidators(resetPasswordSchema.shape.newPassword)}
          >
            {(field) => {
              const localError = validatePasswordForForm(field.state.value)
              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>{m.newPassword()}</Label>
                  <PasswordInput
                    id={field.name}
                    autoComplete="new-password"
                    placeholder={m.passwordPlaceholder()}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <PasswordStrength password={field.state.value} />
                  {field.state.meta.isTouched && field.state.value && localError && (
                    <p className="text-destructive text-sm">{localError}</p>
                  )}
                </div>
              )
            }}
          </form.Field>

          <form.Field
            name="confirmPassword"
            validators={createFieldValidators(resetPasswordSchema.shape.confirmPassword)}
          >
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>{m.confirmPassword()}</Label>
                <PasswordInput
                  id={field.name}
                  autoComplete="new-password"
                  placeholder={m.passwordPlaceholder()}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.isTouched && field.state.meta.errors?.[0] && (
                  <p className="text-destructive text-sm">
                    {typeof field.state.meta.errors[0] === 'string'
                      ? field.state.meta.errors[0]
                      : m.invalidValue()}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {submitError && (
            <div className="squircle rounded-lg border border-destructive/20 bg-destructive/10 p-3">
              <p className="text-destructive text-sm">{submitError}</p>
            </div>
          )}

          <Button
            type="submit"
            variant="brand"
            size="lg"
            className="w-full"
            disabled={setPassword.isPending}
          >
            {setPassword.isPending ? props.submitPendingLabel : props.submitIdleLabel}
          </Button>
        </form>
      ) : (
        <>
          <p
            className={cn(
              'squircle rounded-lg p-3 text-sm',
              sessionError
                ? 'border border-destructive/20 bg-destructive/10 text-destructive'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {sessionError ?? props.noTokenHelp}
          </p>
          <Button asChild variant="brand" size="lg" className="w-full">
            <Link to="/forgot-password" search={{ email: '' }}>
              {m.requestNewLink()}
            </Link>
          </Button>
        </>
      )}
    </AuthShell>
  )
}
